import { Injectable, inject, signal } from '@angular/core';
import { CalAngularService, ICvxClaimsPrincipal } from '@cvx/cal-angular';
import { Observable, tap, catchError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthStateSignalsService {
  private readonly calService = inject(CalAngularService);

  // Private writable signals - internal state
  private _isSignedIn = signal<boolean>(false);
  private _userName = signal<string>('');
  private _claims = signal<ICvxClaimsPrincipal | null>(null);
  private _isLoading = signal<boolean>(false);
  private _error = signal<string | null>(null);

  // Public read-only signals - external interface
  public readonly isSignedIn = this._isSignedIn.asReadonly();
  public readonly userName = this._userName.asReadonly();
  public readonly claims = this._claims.asReadonly();
  public readonly isLoading = this._isLoading.asReadonly();
  public readonly error = this._error.asReadonly();

  constructor() {
    this.checkAuthState();
  }

  /**
   * Re-evaluates the current authentication state against MSAL.
   * Use this when authentication may have completed asynchronously after the
   * service was first constructed (e.g. the sign-in popup finished, or the user
   * logged out and back in) so the UI reflects the real state without a full
   * page refresh.
   */
  refreshAuthState(): void {
    this.checkAuthState();
  }

  private checkAuthState(): void {
    this._isLoading.set(true);
    this._error.set(null);

    this.calService.isUserSignedIn().subscribe({
      next: (isSignedIn: boolean) => {
        this._isSignedIn.set(isSignedIn);

        if (isSignedIn) {
          this.calService
            .getClaims()
            .then((claims) => {
              this._userName.set(claims?.name ?? '');
              this._claims.set(claims);
              this._isLoading.set(false);
            })
            .catch(() => {
              this._error.set('Failed to load user claims');
              this._isLoading.set(false);
            });
        } else {
          this._userName.set('');
          this._claims.set(null);
          this._isLoading.set(false);
        }
      },
      error: () => {
        this._error.set('Failed to check authentication status');
        this._isLoading.set(false);
      },
    });
  }

  signIn(): Observable<unknown> {
    this._isLoading.set(true);
    this._error.set(null);

    return this.calService.userInitiatedSignIn().pipe(
      tap({
        next: (claimsPrincipal) => {
          this._isSignedIn.set(true);
          this._userName.set(claimsPrincipal?.name ?? '');
          this._claims.set(claimsPrincipal);
          this._isLoading.set(false);
        }
      }),
      catchError((error) => {
        this._error.set('Sign in failed');
        this._isLoading.set(false);
        throw error;
      })
    );
  }

  signOut(): Observable<boolean> {
    this._isLoading.set(true);
    this._error.set(null);

    return this.calService.userInitiatedSignOut().pipe(
      tap({
        next: (success: boolean) => {
          if (success) {
            this._isSignedIn.set(false);
            this._userName.set('');
            this._claims.set(null);
          }
          this._isLoading.set(false);
        }
      }),
      catchError((error) => {
        this._error.set('Sign out failed');
        this._isLoading.set(false);
        throw error;
      })
    );
  }
}
